import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  ChevronRight,
  X,
  Pin
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../utils/cn';
import { useDashboardInit } from '../../hooks/useDashboardInit';
import QuotaWidget from '../../views/dashboard/QuotaWidget';
import logo from '../../assets/agent-capital-logo.jpeg';

interface LeftSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isPinned?: boolean;
  onPinToggle?: () => void;
  isHovered?: boolean;
  onHoverChange?: (hovered: boolean) => void;
  isSessionView?: boolean;
}

const LeftSidebar: React.FC<LeftSidebarProps> = ({
  isOpen,
  onClose,
  isPinned = true,
  onPinToggle,
  isHovered = false,
  onHoverChange,
  isSessionView = false
}) => {
  const { org } = useAuth();
  const { data } = useDashboardInit();

  const orgName = org?.name || 'Institutional Curator';
  const showLogo = orgName === 'org-acme-001';

  return (
    <aside 
      onMouseEnter={() => {
        if (isSessionView && !isPinned) {
          onHoverChange?.(true);
        }
      }}
      onMouseLeave={() => {
        if (isSessionView && !isPinned) {
          onHoverChange?.(false);
        }
      }}
      className={cn(
        "fixed inset-y-0 left-0 w-64 bg-surface-container-lowest flex flex-col shadow-ambient z-30 transition-transform duration-300",
        isSessionView
          ? cn(
              isOpen ? "translate-x-0" : "-translate-x-full",
              isPinned ? "md:translate-x-0" : (isHovered ? "md:translate-x-0" : "md:-translate-x-full")
            )
          : (isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0")
      )}
    >
      {/* Brand Header */}
      <div className="p-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {showLogo && (
            <img src={logo} alt="Agent Capital Logo" className="w-8 h-8 rounded-lg object-cover" />
          )}
          <div>
            <h1 className="font-display font-black text-lg tracking-tight leading-none text-navy">
              {orgName}
            </h1>
          </div>
        </div>

        {isSessionView && (
          <button
            onClick={onPinToggle}
            className={cn(
              "hidden md:block p-2 text-secondary hover:text-on-surface hover:bg-surface-container-low rounded-lg transition-all",
              isPinned ? "text-primary bg-primary/10" : "text-secondary opacity-60"
            )}
            title={isPinned ? "Unpin sidebar" : "Pin sidebar"}
            aria-label={isPinned ? "Unpin sidebar" : "Pin sidebar"}
          >
            <Pin size={16} className={cn("transition-transform duration-200", !isPinned && "-rotate-45")} />
          </button>
        )}

        <button 
          onClick={onClose}
          className="p-2 md:hidden text-secondary hover:text-on-surface transition-colors"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-8 overflow-y-auto">
        <div className="space-y-1">
          <NavLink
            to="/"
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group",
              isActive 
                ? "bg-primary-fixed/50 text-primary font-bold shadow-sm" 
                : "text-secondary hover:bg-surface-container-low hover:text-on-surface"
            )}
          >
            <LayoutDashboard size={20} />
            <span className="text-sm">Dashboard</span>
          </NavLink>
        </div>

        {/* Agents Section */}
        <div className="space-y-2">
          <h2 className="px-4 text-[10px] font-black uppercase tracking-widest text-on-surface-variant/40">
            Active Agents
          </h2>
          <div className="space-y-1">
            {data?.enabledAgents.map((agent) => (
              <NavLink
                key={agent.name}
                to={`/session/${agent.name}`}
                className={({ isActive }) => cn(
                  "flex items-center justify-between px-4 py-2.5 rounded-xl text-sm transition-all group",
                  isActive
                    ? "bg-surface-container-high text-primary font-semibold"
                    : "text-secondary hover:bg-surface-container-low hover:text-on-surface"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary/40 group-hover:bg-primary transition-colors" />
                  <span>{agent.name}</span>
                </div>
                <ChevronRight size={14} className="opacity-0 group-hover:opacity-40 transition-opacity" />
              </NavLink>
            ))}
          </div>
        </div>
      </nav>

      {/* Powered by Branding & Quota Widget */}
      <div className="px-4 py-6 border-t border-outline-variant/10 flex flex-col gap-4">
        <div className="text-center py-1">
          <span className="text-[10px] font-bold text-secondary/40 tracking-widest uppercase">
            Demo Site - Powered by Agent Capital
          </span>
        </div>
        {data?.quota && (
          <QuotaWidget
            used={data.quota.interaction.used}
            limit={data.quota.interaction.limit}
            nextResetDate={data.quota.interaction.nextResetDate}
          />
        )}
      </div>


    </aside>
  );
};

export default LeftSidebar;
